import { cn } from '../../lib/utils';

interface ReflexMarkProps {
  className?: string;
  /**
   * - `bare` (default): just the three chevrons, transparent background.
   *   Use inline with wordmarks or in dense UI.
   * - `tile`: chevron centered inside a dark rounded-square with a
   *   hairline ring. Matches the PWA app tile — use when the mark
   *   needs to feel self-contained (navbars, empty states, etc).
   */
  variant?: 'bare' | 'tile';
}

/**
 * Reflex brand mark — kinetic echo-trail chevron (K3b).
 *
 * Three stacked chevrons, each slightly larger and brighter than the
 * last: the trail is the motion history, the front is the current
 * state. Flat tri-tone green, no opacity, no gradients — colors are
 * deliberate palette steps rather than transparency tricks.
 *
 * Palette:
 *  - #14463a  (back)   — deepest echo, nearly merges with dark bg
 *  - #2a7a63  (mid)    — first echo, reduced saturation/brightness
 *  - #68ecc4  (front)  — primary brand green
 *
 * Colors are baked in (this is a brand mark, not a themeable icon).
 * Callers control size via `className`; color classes are a no-op.
 *
 * See docs/design/branding.md for the full brand guide.
 */
export function ReflexMark({ className, variant = 'bare' }: ReflexMarkProps) {
  const glyph = (
    <svg
      data-testid="reflex-mark-glyph"
      data-variant={variant}
      viewBox="0 0 24 24"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={variant === 'tile' ? 'h-[70%] w-[70%]' : cn('h-4 w-4', className)}
    >
      <path stroke="#14463a" strokeWidth="2.2" d="M 3 8 L 7 12 L 3 16" />
      <path stroke="#2a7a63" strokeWidth="2.4" d="M 8 6 L 14 12 L 8 18" />
      <path stroke="#68ecc4" strokeWidth="2.8" d="M 13 4 L 21 12 L 13 20" />
    </svg>
  );

  if (variant === 'tile') {
    return (
      <span
        data-reflex-mark="tile"
        data-testid="reflex-mark-tile"
        className={cn(
          'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[22%] bg-[#14131a] ring-1 ring-inset ring-[#1f1d27]',
          className,
        )}
      >
        {glyph}
      </span>
    );
  }

  return glyph;
}
